import React from 'react';
import { Admin, Resource } from 'react-admin';
import { Route } from 'react-router-dom';

import { i18nProvider } from '~config/i18n';
import authProvider from '~config/providers/authProvider';
import dataProvider from '~config/providers/dataProvider';
import addImageUpload from '~config/providers/addImageUpload';
import CustomLayout from '~components/CustomLayout';
import IirsaProjectList from '~screens/IirsaProjects/IirsaProjectList';
import IirsaProjectCreate from '~screens/IirsaProjects/IirsaProjectCreate';
import IirsaProjectEdit from '~screens/IirsaProjects/IirsaProjectEdit';
import IirsaProjectShow from '~screens/IirsaProjects/IirsaProjectShow';
import DraftProjectList from '~screens/DraftProjects/DraftProjectList';
import DraftProjectCreate from '~screens/DraftProjects/DraftProjectCreate';
import DraftProjectEdit from '~screens/DraftProjects/DraftProjectEdit';

import '../scss/quillOverrides.scss';
import '../scss/typography.scss';

import styles from './styles.module.scss'; // eslint-disable-line no-unused-vars
import { appTheme } from './styles';
import Login from './screens/Login';
import RoutesList from "./screens/Routes/RoutesList";
import RouteCreate from "./screens/Routes/RouteCreate";
import RouteShow from "./screens/Routes/RouteShow";
import RouteEdit from "./screens/Routes/RouteEdit";
import PriorizacionList from "~screens/Priorizaciones/PriorizacionList";
import PriorizacionCreate from "~screens/Priorizaciones/PriorizacionCreate";
import PriorizacionEdit from "~screens/Priorizaciones/PriorizacionEdit";
import DisclaimerList from "~screens/Disclaimers/DisclaimerList";
import DisclaimerCreate from "~screens/Disclaimers/DisclaimerCreate";
import DisclaimerEdit from "~screens/Disclaimers/DisclaimerEdit";
import EtapaList from "~screens/Etapas/EtapaList";
import EtapaEdit from "~screens/Etapas/EtapaEdit";
import AlcanceList from "~screens/Alcances/AlcanceList";
import AlcanceEdit from "~screens/Alcances/AlcanceEdit";
import AlcanceCreate from "~screens/Alcances/AlcanceCreate";
import EstadoEstudiosList from "~screens/EstadosEstudios/EstadoEstudiosList";
import EstadoEstudiosEdit from "~screens/EstadosEstudios/EstadoEstudiosEdit";
import EstadoEstudiosCreate from "~screens/EstadosEstudios/EstadoEstudiosCreate";
import EstadoList from "~screens/Estados/EstadoList";
import EstadoEdit from "~screens/Estados/EstadoEdit";
import EstadoCreate from "~screens/Estados/EstadoCreate";
import FinanciadorEstudiosList from "~screens/FinanciadoresEstudios/FinanciadorEstudiosList";
import FinanciadorEstudiosEdit from "~screens/FinanciadoresEstudios/FinanciadorEstudiosEdit";
import FinanciadorEstudiosCreate from "~screens/FinanciadoresEstudios/FinanciadorEstudiosCreate";
import TipoProyectoList from "~screens/TiposProyecto/TipoProyectoList";
import TipoProyectoEdit from "~screens/TiposProyecto/TipoProyectoEdit";
import TipoProyectoCreate from "~screens/TiposProyecto/TipoProyectoCreate";
import SectorList from "~screens/Sectores/SectorList";
import SectorEdit from "~screens/Sectores/SectorEdit";
import SectorCreate from "~screens/Sectores/SectorCreate";
import SubsectorList from "~screens/Subsectores/SubsectorList";
import SubsectorEdit from "~screens/Subsectores/SubsectorEdit";
import SubsectorCreate from "~screens/Subsectores/SubsectorCreate";
import PaisList from "~screens/Paises/PaisList";
import PaisEdit from "~screens/Paises/PaisEdit";
import PaisCreate from "~screens/Paises/PaisCreate";
import MetricList from "~screens/Metrics/MetricList";
import MetricCreate from "~screens/Metrics/MetricCreate";
import MetricEdit from "~screens/Metrics/MetricEdit";
import MetricValueList from "~screens/MetricValues/MetricValueList";
import MetricValueCreate from "~screens/MetricValues/MetricValueCreate";
import MetricValueEdit from "~screens/MetricValues/MetricValueEdit";
import EtapaWeights from "~screens/Etapas/EtapaWeights";
import ProjectTrackingEdit from "~screens/ProjectTracking/ProjectTrackingEdit";
import LayersList from "~screens/Layers/LayersList";
import ProjectsLayerScreen from "~screens/ProjectsLayer";
import LayerCreate from "~screens/Layers/LayerCreate";
import LayerEdit from "~screens/Layers/LayerEdit";
import UserList from "~screens/Users/UserList";
import UserCreate from "~screens/Users/UserCreate";
import UserEdit from "~screens/Users/UserEdit";
import {
  can,
  ADD_PROYECTO,
  CHANGE_PROYECTO,
  VIEW_DRAFT,
  VIEW_USER
} from '~utils/permissions';

function App() {
  return (
    <Admin
      theme={appTheme}
      locale="es"
      i18nProvider={i18nProvider}
      dataProvider={addImageUpload(dataProvider)}
      authProvider={authProvider}
      appLayout={CustomLayout}
      loginPage={Login}
      // Reached from the Etapas list, which is only registered for users who may
      // change etapas; the API enforces the same permission.
      customRoutes={[<Route key="etapas-weights" exact path="/etapas/weights" component={EtapaWeights} />]}
    >
      {/* Function-as-children so resources follow the caller's capabilities. It runs
          once with `permissions` undefined, before AUTH_GET_PERMISSIONS resolves --
          `can()` tolerates that. A resource declared WITHOUT list/edit/create stays
          available to ReferenceInput lookups but gets no route and no menu entry,
          which is how a country manager still gets working form dropdowns. */}
      {permissions => {
        const parametric = (name, list, edit, create) =>
          can(permissions, `portfolio.change_${name.model}`)
            ? <Resource key={name.resource} name={name.resource} list={list} edit={edit} create={create} />
            : <Resource key={name.resource} name={name.resource} />;

        const resources = [
          can(permissions, 'portfolio.change_ruta')
            ? <Resource key="routes" name="routes" list={RoutesList} create={RouteCreate} show={RouteShow} edit={RouteEdit} />
            : <Resource key="routes" name="routes" />,
          <Resource
            key="projects"
            name="projects"
            show={IirsaProjectShow}
            list={IirsaProjectList}
            create={can(permissions, ADD_PROYECTO) ? IirsaProjectCreate : null}
            edit={can(permissions, CHANGE_PROYECTO) ? IirsaProjectEdit : null}
          />,
          // Edit-only, keyed by project code and opened from the project Show screen.
          can(permissions, CHANGE_PROYECTO) && (
            <Resource key="project-tracking" name="project-tracking" edit={ProjectTrackingEdit} />
          ),
          can(permissions, VIEW_DRAFT) && (
            <Resource
              key="draft-projects"
              name="draft-projects"
              options={{ label: 'Proyectos (Borrador)' }}
              list={DraftProjectList}
              create={DraftProjectCreate}
              edit={DraftProjectEdit}
            />
          ),
          parametric({ resource: 'priorizaciones', model: 'priorizacion' }, PriorizacionList, PriorizacionEdit, PriorizacionCreate),
          parametric({ resource: 'disclaimers', model: 'disclaimer' }, DisclaimerList, DisclaimerEdit, DisclaimerCreate),
          parametric({ resource: 'etapas', model: 'etapa' }, EtapaList, EtapaEdit),
          parametric({ resource: 'alcances', model: 'alcance' }, AlcanceList, AlcanceEdit, AlcanceCreate),
          parametric({ resource: 'estados-estudios', model: 'estadoestudios' }, EstadoEstudiosList, EstadoEstudiosEdit, EstadoEstudiosCreate),
          parametric({ resource: 'estados', model: 'estado' }, EstadoList, EstadoEdit, EstadoCreate),
          parametric({ resource: 'financiadores-estudios', model: 'financiadorestudios' }, FinanciadorEstudiosList, FinanciadorEstudiosEdit, FinanciadorEstudiosCreate),
          parametric({ resource: 'tipos-proyecto', model: 'tipoproyecto' }, TipoProyectoList, TipoProyectoEdit, TipoProyectoCreate),
          parametric({ resource: 'sectores', model: 'sector' }, SectorList, SectorEdit, SectorCreate),
          parametric({ resource: 'subsectores', model: 'subsector' }, SubsectorList, SubsectorEdit, SubsectorCreate),
          parametric({ resource: 'paises', model: 'pais' }, PaisList, PaisEdit, PaisCreate),
          parametric({ resource: 'metrics', model: 'metric' }, MetricList, MetricEdit, MetricCreate),
          parametric({ resource: 'metric-values', model: 'metricvalue' }, MetricValueList, MetricValueEdit, MetricValueCreate),
          parametric({ resource: 'layers', model: 'layer' }, LayersList, LayerEdit, LayerCreate),
          // Single config row, so the "list" is the edit screen itself. It talks to
          // the API directly rather than through the dataProvider.
          can(permissions, 'portfolio.change_projectslayer') && (
            <Resource key="projects-layer" name="projects-layer" list={ProjectsLayerScreen} />
          ),
          can(permissions, VIEW_USER) && (
            <Resource
              key="users"
              name="users"
              options={{ label: 'Usuarios' }}
              list={UserList}
              create={UserCreate}
              edit={UserEdit}
            />
          ),
          // Reference-only: needed by ReferenceInputs for every role.
          <Resource key="countries" name="countries" />
        ].filter(Boolean);

        // react-admin sends `/` to the FIRST registered resource. For a role that only
        // has `routes` as a reference-only resource (no list), that is a blank page, so
        // list-bearing resources go first. Order is unchanged for roles that can list
        // everything.
        return [
          ...resources.filter(resource => resource.props.list),
          ...resources.filter(resource => !resource.props.list)
        ];
      }}
    </Admin>
  );
}

export default App;
